import { jsx as _jsx } from "react/jsx-runtime";
import classNames from "classnames";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useReducer, useRef, useState } from "react";
import { TreePadContent } from "./components/tree/padContent.js";
import { useFolderTree } from "./contexts/folderTree.js";
import { FolderTreeRuntimeProvider } from "./contexts/folderTreeRuntimeProvider.js";
import { useTreeInteractionProvider } from "./contexts/treeInteraction.js";
import { useSortableTree } from "./hooks/tree/useSortableTree.js";
import { useTreeSearch } from "./hooks/tree/useTreeSearch.js";
import { useTreeStorage } from "./hooks/tree/useTreeStorage.js";
import { useDebouncedTreeRefresh } from "./hooks/useDebouncedTreeRefresh.js";
import { useFolderTreeSort } from "./hooks/useFolderTreeSort.js";
import { useTreeExpansion } from "./hooks/useTreeExpansion.js";
import { getRecursivelyCheckedNodes, uuid } from "./util/tree.js";
const Tree = /*#__PURE__*/ forwardRef((layoutProps, ref)=>{
    const { theme = "default", id: propId, className, innerClassName, style = {}, attr = {}, isSticky = false, isStickyHeader = false, treeStickyAttr = {}, headerStickyAttr = {}, isResizable = true, isFullWidth = false, defaultWidth = 250, minWidth = 250, maxWidth = 800, opposite, oppositeOffset = 16, sortableDelay = 100, thresholdPx = 5, isSortable = false, forceSortableFallback = false, children } = layoutProps;
    const { tree = [], toolbarActiveButton, onSearchResult, onSelect, onCheck, onCloseMultiToolbar, onNodeExpand, onSortStart, onSortEnd, onSortMove, onResize, onResizeFinished, onResizeOpposite, onTreeStructureChanged } = useFolderTree();
    const onSort = useFolderTreeSort();
    const [instanceUuid] = useState(()=>uuid());
    const treeDomId = propId || instanceUuid;
    const containerRef = useRef(null);
    const storage = useTreeStorage(propId);
    const [, bumpRender] = useReducer((revision)=>revision + 1, 0);
    const [collapsed, setCollapsed] = useState(false);
    const [stickyTreeCalculatedTop, setStickyTreeCalculatedTop] = useState(undefined);
    const [renamingNodeId, setRenamingNodeId] = useState(undefined);
    useImperativeHandle(ref, ()=>({
            container: containerRef.current
        }), []);
    const emitTreeStructureChanged = useDebouncedTreeRefresh(()=>{
        onTreeStructureChanged?.();
    }, 200);
    const handleNodeExpandWithRefresh = useCallback((expanded, node)=>{
        onNodeExpand?.(expanded, node);
        emitTreeStructureChanged();
    }, [
        emitTreeStructureChanged,
        onNodeExpand
    ]);
    const handleSearchResultWithRefresh = useCallback((result)=>{
        onSearchResult?.(result);
        emitTreeStructureChanged();
    }, [
        emitTreeStructureChanged,
        onSearchResult
    ]);
    const handleSortEndWithRefresh = useCallback((evt)=>{
        onSortEnd?.(evt);
        emitTreeStructureChanged();
    }, [
        emitTreeStructureChanged,
        onSortEnd
    ]);
    const { getExpandedState, getExpandedNodes, handleNodeExpand, handleToggleAll } = useTreeExpansion(storage, tree, handleNodeExpandWithRefresh, bumpRender);
    const handleCheck = useCallback((nodeId)=>{
        onCheck?.(nodeId);
        bumpRender();
    }, [
        onCheck
    ]);
    const handleCloseMultiToolbar = useCallback(()=>{
        onCloseMultiToolbar?.(getRecursivelyCheckedNodes(tree));
        bumpRender();
    }, [
        onCloseMultiToolbar,
        tree
    ]);
    const search = useTreeSearch({
        tree,
        treeDomId,
        onSearchResult: handleSearchResultWithRefresh,
        onSelect
    });
    const { registerSortableUl } = useSortableTree({
        enabled: !!isSortable,
        treeId: treeDomId,
        sortableDelay,
        thresholdPx,
        forceSortableFallback,
        onSort,
        onSortStart,
        onSortEnd: handleSortEndWithRefresh,
        onSortMove
    });
    useEffect(()=>{
        const headerEl = document.querySelector(`#${treeDomId} .aiot-fixed-header > div`);
        let newTreeCalculatedTop = 0;
        if (isSticky && isStickyHeader && typeof treeStickyAttr.top === "undefined" && headerEl) {
            newTreeCalculatedTop = headerEl.offsetHeight;
            const headerStickyTop = headerStickyAttr.top;
            if (typeof headerStickyTop === "string") {
                const headerStickyTopEl = document.querySelector(headerStickyTop);
                newTreeCalculatedTop += headerStickyTopEl ? headerStickyTopEl.offsetHeight : 0;
            } else if (typeof headerStickyTop === "number") {
                newTreeCalculatedTop += headerStickyTop;
            }
        }
        setStickyTreeCalculatedTop(newTreeCalculatedTop);
    }, [
        headerStickyAttr,
        isSticky,
        isStickyHeader,
        treeDomId,
        treeStickyAttr.top
    ]);
    const handleResize = useCallback((x, nextCollapsed)=>{
        setCollapsed(nextCollapsed);
        onResize?.(x, nextCollapsed);
    }, [
        onResize
    ]);
    const handleResizeFinished = useCallback((width)=>{
        storage.setItem("width", width);
        width > 0 && storage.setItem("rwidth", width);
        onResizeFinished?.(width);
    }, [
        onResizeFinished,
        storage
    ]);
    const [TreeInteractionProvider, treeInteractionValue] = useTreeInteractionProvider({
        collapsed,
        stickyTreeCalculatedTop,
        searchTerm: search.searchTerm,
        resultSelectedNodeIdx: search.resultSelectedNodeIdx,
        resultTreeBusy: search.resultTreeBusy,
        resultTree: search.resultTree,
        renamingNodeId
    }, {
        handleSearchChange: search.handleSearchChange,
        handleSearchClose: search.handleSearchClose,
        handleSearchBlur: search.handleSearchBlur,
        handleSearchKeyDown: search.handleSearchKeyDown,
        getExpandedState,
        getExpandedNodes,
        handleNodeExpand,
        handleCheck,
        handleToggleAll,
        handleCloseMultiToolbar,
        registerSortableUl,
        setRenamingNodeId
    });
    const checkedNodes = getRecursivelyCheckedNodes(tree);
    const rootClassName = classNames("aiot-tree", className, `aiot-theme-${theme}`, {
        "aiot-wrap-collapse": collapsed,
        "aiot-full-width": isFullWidth,
        "aiot-has-checked": checkedNodes.length > 0,
        "aiot-toolbar-active-button": toolbarActiveButton
    });
    const rootStyle = {
        ...style,
        ...!isFullWidth && {
            width: `${defaultWidth}px`,
            minWidth: `${minWidth}px`,
            maxWidth: `${maxWidth}px`
        }
    };
    return /*#__PURE__*/ _jsx(TreeInteractionProvider, {
        value: treeInteractionValue,
        children: /*#__PURE__*/ _jsx(FolderTreeRuntimeProvider, {
            children: /*#__PURE__*/ _jsx("div", {
                id: treeDomId,
                style: rootStyle,
                className: rootClassName,
                ref: containerRef,
                ...attr,
                children: /*#__PURE__*/ _jsx(TreePadContent, {
                    treeDomId: treeDomId,
                    isSticky: !!isSticky,
                    isStickyHeader: !!isStickyHeader,
                    headerStickyAttr: headerStickyAttr,
                    treeStickyAttr: treeStickyAttr,
                    isResizable: !!isResizable,
                    opposite: opposite,
                    minWidth: minWidth,
                    maxWidth: maxWidth,
                    collapsed: collapsed,
                    oppositeOffset: oppositeOffset,
                    innerClassName: innerClassName,
                    storage: storage,
                    onResize: handleResize,
                    onResizeOpposite: onResizeOpposite,
                    onResizeFinished: handleResizeFinished,
                    children: children
                })
            })
        })
    });
});
export { Tree };
